import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { QuizProps } from '../../types';
import TestItem from '../test-item/TestItem';

const Container = styled.div`
  display: block;
  margin-top: 40px;
`;

const Title = styled.h2`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 24px;
  line-height: 36px;
  font-weight: 700;
`;

const ItemCounter = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  padding-inline: 8px;
  background-color: var(--gray-03);
  border-radius: 8px;
`;

const List = styled.ul<{ columns: number }>`
  list-style: none;
  display: grid;
  grid-template-columns: repeat(${(props) => props.columns}, 1fr);
  margin-top: 14px;
  gap: 20px;
`;

const ListHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ShowAllButton = styled.button`
  font-size: 16px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 2px;
    width: 100%;
    height: 1px;
    background-color: var(--white);
    transition: width 0.3s ease;
  }
`;

const DESKTOP_COLUMNS = 4;
const TABLET_COLUMNS = 3;
const MOBILE_COLUMNS = 1;
const DESKTOP_DISPLAY_LENGTH = 4;
const TABLET_DISPLAY_LENGTH = 3;
const MOBILE_DISPLAY_LENGTH = 3;

type Props = {
  tests: QuizProps[];
  label: string;
};

const TestsList: React.FC<Props> = ({ tests, label }) => {
  const [displayLength, setDisplayLength] = useState(DESKTOP_DISPLAY_LENGTH);
  const [columns, setColumns] = useState(DESKTOP_COLUMNS);

  useEffect(() => {
    const updateLayout = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setColumns(MOBILE_COLUMNS);
        setDisplayLength(MOBILE_DISPLAY_LENGTH);
      } else if (width < 768) {
        setColumns(TABLET_COLUMNS);
        setDisplayLength(TABLET_DISPLAY_LENGTH);
      } else {
        setColumns(DESKTOP_COLUMNS);
        setDisplayLength(DESKTOP_DISPLAY_LENGTH);
      }
    };

    window.addEventListener('resize', updateLayout);
    updateLayout();

    return () => {
      window.removeEventListener('resize', updateLayout);
    };
  }, []);

  if (!tests.length) return null;

  const isMoreThanDisplayable = tests.length > displayLength;
  const displayShowButton = displayLength !== tests.length && isMoreThanDisplayable;
  const stringTestsLength = String(tests.length).padStart(2, '0');

  return (
    <Container>
      <ListHeader>
        <Title>
          {label}
          {isMoreThanDisplayable && <ItemCounter>{stringTestsLength}</ItemCounter>}
        </Title>
        {displayShowButton && (
          <ShowAllButton onClick={() => setDisplayLength(tests.length)}>Ver todos</ShowAllButton>
        )}
      </ListHeader>
      <List columns={columns}>
        {tests
          .slice(0, displayLength)
          .map(({ guid, title, introduction, score, tags, examExercisesConfig }) => (
            <TestItem
              key={guid}
              guid={guid}
              title={title}
              description={introduction}
              score={score}
              tags={tags}
              startDate={examExercisesConfig.start_date}
            />
          ))}
      </List>
    </Container>
  );
};

export default TestsList;
